import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionDrawer, { UI, btnSx } from "../components/ActionDrawer";
import DashTable, { PageHead, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const sameDay = (a, b) =>
  a.getDate() === b.getDate() &&
  a.getMonth() === b.getMonth() &&
  a.getFullYear() === b.getFullYear();

/* Monday to Friday of the week `weekOffset` weeks from the current one. */
const weekDaysFor = (weekOffset) => {
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7) + weekOffset * 7);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
};

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

/* OL5. A job appears only once a technician has accepted it. */
const TravelPlan = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [jobs, setJobs] = useState([]);
  const [opsLeadSummary, setOpsLeadSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [active, setActive] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);

  const fetchRows = async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const [planRes, summaryRes] = await Promise.all([
        apiService.fetchTravelPlan(token),
        apiService.fetchOpsLeadSummary(token),
      ]);
      setJobs(planRes.data || []);
      setOpsLeadSummary(summaryRes.data || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching the travel plan");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toItem = (row, id, screenKey) => ({
    id,
    screenKey,
    orderId: row.orderId,
    orderNumber: row.orderNumber,
    clinicName: row.clinicName,
    context: {
      city: row.city,
      technicianName: row.technicianName,
      arrival: row.arrivalDate ? shortDate(row.arrivalDate) : null,
      engagementOfficer: row.engagementOfficer,
    },
  });

  const days = weekDaysFor(weekOffset);
  const isCurrentWeek = weekOffset === 0;
  const weekRangeLabel = `${days[0].toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – ${days[
    days.length - 1
  ].toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`;

  /* Shared across every Ops Lead page — not recomputed per page. */
  const kpis = opsLeadSummary
    ? [
        { label: "Needs your action", value: opsLeadSummary.needsAction },
        { label: "Blocking", value: opsLeadSummary.blocking, alarm: true },
        { label: "Jobs in flight", value: opsLeadSummary.jobsInFlight },
        { label: "Unassigned", value: opsLeadSummary.unassigned },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Travel plan"
        lede={
          isCurrentWeek
            ? "This week. A job appears here only once a technician has accepted it."
            : "A job appears here only once a technician has accepted it."
        }
        kpis={kpis}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "12px",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => setWeekOffset((w) => w - 1)}
          sx={{
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "6px",
            bgcolor: UI.surface,
            color: UI.ink,
            fontSize: 13,
            padding: { xs: "10px 14px", sm: "4px 10px" },
            minHeight: { xs: "42px", sm: "auto" },
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          ‹ Previous
        </Box>
        <Box sx={{ fontSize: 13, color: UI.ink3, minWidth: 90, textAlign: "center" }}>
          {weekRangeLabel}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => setWeekOffset((w) => w + 1)}
          sx={{
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "6px",
            bgcolor: UI.surface,
            color: UI.ink,
            fontSize: 13,
            padding: { xs: "10px 14px", sm: "4px 10px" },
            minHeight: { xs: "42px", sm: "auto" },
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Next ›
        </Box>
        {!isCurrentWeek && (
          <Box
            component="button"
            type="button"
            onClick={() => setWeekOffset(0)}
            sx={{
              border: "none",
              bgcolor: "transparent",
              color: UI.p,
              fontSize: 12,
              cursor: "pointer",
              fontFamily: "inherit",
              textDecoration: "underline",
            }}
          >
            Today
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
          gap: "10px",
          marginBottom: "28px",
        }}
      >
        {days.map((day) => {
          const events = jobs.filter(
            (j) => j.arrivalDate && sameDay(new Date(j.arrivalDate), day),
          );
          return (
            <Box
              key={day.toISOString()}
              sx={{
                bgcolor: UI.surface,
                border: "1px solid",
                borderColor: UI.line,
                borderRadius: "8px",
                minHeight: { xs: "auto", sm: 150 },
              }}
            >
              <Box
                sx={{
                  padding: { xs: "10px 13px", sm: "9px 11px" },
                  borderBottom: "1px solid",
                  borderBottomColor: UI.line,
                  bgcolor: UI.bg2,
                  fontSize: { xs: 13, sm: 12 },
                  fontWeight: 500,
                  color: UI.ink,
                }}
              >
                {day.toLocaleDateString("en-IN", { weekday: "short" })}
                <Box
                  component="small"
                  sx={{
                    display: "block",
                    color: UI.ink3,
                    fontWeight: 400,
                    fontSize: { xs: 12, sm: 11 },
                  }}
                >
                  {day.toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                  })}
                </Box>
              </Box>
              <Box sx={{ padding: "8px" }}>
                {events.length === 0 ? (
                  <Box sx={{ fontSize: { xs: 12, sm: 11 }, color: UI.ink4 }}>
                    Nothing scheduled
                  </Box>
                ) : (
                  events.map((j) => (
                    <Box
                      key={j.orderId}
                      onClick={() => navigate(`/dashboard/orders/${j.orderId}`)}
                      sx={{
                        bgcolor: UI.p50,
                        borderLeft: "2px solid",
                        borderLeftColor: UI.p,
                        borderRadius: "0 4px 4px 0",
                        padding: { xs: "10px 12px", sm: "7px 9px" },
                        marginBottom: "7px",
                        fontSize: { xs: 13, sm: 11 },
                        cursor: "pointer",
                        "&:hover": { bgcolor: UI.p100 },
                      }}
                    >
                      <Box sx={{ fontWeight: 500, color: UI.pDark }}>
                        {j.technicianName} → {(j.city || "").split(",")[0]}
                      </Box>
                      <Box sx={{ color: UI.ink3, marginTop: "2px" }}>
                        {j.clinicName}
                        {j.slot ? `, ${j.slot}` : ""}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <DashTable
        columns={[
          { label: "Clinic" },
          { label: "City" },
          { label: "Technician" },
          { label: "Arrival" },
          { label: "Inauguration" },
          { label: "Action" },
        ]}
        rows={jobs.map((j) => ({
          id: j.orderId,
          cells: [
            j.clinicName,
            j.city,
            j.technicianName || <Tag tone="b">Unassigned</Tag>,
            j.arrivalDate ? shortDate(j.arrivalDate) : <Tag tone="s">Not set</Tag>,
            j.inaugurationDate ? (
              shortDate(j.inaugurationDate)
            ) : (
              <Tag tone="i">Not set</Tag>
            ),
            !j.arrivalDate ? (
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActive(toItem(j, "OL5", "plan-travel"));
                }}
                sx={btnSx("ghost")}
              >
                Plan
              </Box>
            ) : !j.inaugurationDate ? (
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActive(toItem(j, "OL6", "confirm-inauguration"));
                }}
                sx={btnSx("ghost")}
              >
                Set inauguration
              </Box>
            ) : (
              "—"
            ),
          ],
        }))}
        onRowClick={(id) => navigate(`/dashboard/orders/${id}`)}
        emptyText="No accepted jobs yet."
        errorText={loadFailed ? "Could not load the travel plan." : null}
        onRetry={fetchRows}
        foot={`${jobs.length} accepted job${jobs.length === 1 ? "" : "s"}`}
      />

      <ActionDrawer
        open={Boolean(active)}
        screenKey={active?.screenKey}
        item={active}
        onClose={() => setActive(null)}
        onSaved={() => {
          setActive(null);
          fetchRows();
        }}
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default TravelPlan;
